import ClearIcon from "@mui/icons-material/Clear";
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import {
  useGetDashboardCollections,
  useGetDashboardQrs,
} from "@/api/stats.api";

import { PERIOD_PRESETS, PeriodPreset } from "./period-preset";
import { formatQrFilterOption } from "./qr-filter";

export interface PeriodFiltersValue {
  from: Date;
  to: Date;
  cityIds: number[];
  qrfyIds: number[];
  preset: PeriodPreset | null;
}

interface PeriodFiltersProps {
  value: PeriodFiltersValue;
  onChange: (next: PeriodFiltersValue) => void;
  // Top 5 vs all is a client-side display toggle (no refetch).
  topMode: "top5" | "all";
  onTopModeChange: (mode: "top5" | "all") => void;
}

/**
 * Official launch date of Cryptors app.
 */
export const PERIOD_MIN_DATE = new Date(2024, 8, 21);

export const PeriodFilters = ({
  value,
  onChange,
  topMode,
  onTopModeChange,
}: PeriodFiltersProps) => {
  const { data: cities = [] } = useGetDashboardCollections();
  const { data: qrs = [], isLoading: qrsLoading } = useGetDashboardQrs(
    value.cityIds,
  );
  const qrLabel = (qr: (typeof qrs)[number]) => formatQrFilterOption(qr);
  const today = new Date();

  return (
    <Paper sx={{ p: 2 }} elevation={1}>
      <Stack gap={2}>
        <ButtonGroup size="small" variant="outlined">
          {Object.values(PeriodPreset).map((pp) => {
            const p = PERIOD_PRESETS[pp];

            return (
              <Button
                key={p.label}
                variant={value.preset === pp ? "contained" : "outlined"}
                aria-pressed={value.preset === pp}
                onClick={() => onChange({ ...value, ...p.range(), preset: pp })}
              >
                {p.label}
              </Button>
            );
          })}
        </ButtonGroup>
        <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
          <DatePicker
            label="Du"
            value={value.from}
            minDate={PERIOD_MIN_DATE}
            maxDate={value.to < today ? value.to : today}
            onChange={(d) =>
              d &&
              d >= PERIOD_MIN_DATE &&
              d <= value.to &&
              d <= today &&
              onChange({ ...value, from: d, preset: null })
            }
            format="dd/MM/yyyy"
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="Au"
            value={value.to}
            minDate={
              value.from > PERIOD_MIN_DATE ? value.from : PERIOD_MIN_DATE
            }
            maxDate={today}
            onChange={(d) =>
              d &&
              d >= PERIOD_MIN_DATE &&
              d >= value.from &&
              d <= today &&
              onChange({ ...value, to: d, preset: null })
            }
            format="dd/MM/yyyy"
            slotProps={{ textField: { size: "small" } }}
          />
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="collections-label">Collections</InputLabel>
            <Select
              labelId="collections-label"
              multiple
              value={value.cityIds}
              onChange={(e) =>
                onChange({
                  ...value,
                  cityIds:
                    typeof e.target.value === "string"
                      ? []
                      : (e.target.value as number[]),
                  qrfyIds: [],
                })
              }
              input={<OutlinedInput label="Collections" />}
              endAdornment={
                value.cityIds.length ? (
                  <IconButton
                    aria-label="Désélectionner toutes les collections"
                    size="small"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      onChange({ ...value, cityIds: [], qrfyIds: [] });
                    }}
                    sx={{ mr: 2 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null
              }
              renderValue={(selected) =>
                selected.length === 0 ? (
                  "Ensemble"
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => (
                      <Chip
                        key={id}
                        size="small"
                        label={cities.find((c) => c.id === id)?.name ?? id}
                      />
                    ))}
                  </Box>
                )
              }
            >
              {cities.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 320, maxWidth: 600 }}
            options={qrs}
            value={qrs.filter((qr) => value.qrfyIds.includes(qr.id))}
            loading={qrsLoading}
            filterSelectedOptions
            getOptionLabel={qrLabel}
            isOptionEqualToValue={(option, selected) =>
              option.id === selected.id
            }
            onChange={(_, selected) =>
              onChange({
                ...value,
                qrfyIds: selected.map((qr) => qr.id),
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Nom du QR code"
                placeholder={value.qrfyIds.length === 0 ? "Ensemble" : ""}
              />
            )}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={topMode}
            onChange={(_, mode) =>
              mode && onTopModeChange(mode as "top5" | "all")
            }
          >
            <ToggleButton value="top5">Top 5</ToggleButton>
            <ToggleButton value="all">Ensemble</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
    </Paper>
  );
};
